import React, { useState, useEffect } from "react";
import Button from "../Button/Button";
import Icon from "../Icon/Icon";
import * as utils from "../../utils/utils";
import { useStore } from "../user/UserContext";

interface INotification {
  id: number;
  message: string;
  type: "action" | "error" | "alert";
  created: string;
  status: string;
}

interface INotificationPanelProps {
  notifications: INotification[];
  onClose: Function;
}

function NotificationPanel({ notifications, onClose }: INotificationPanelProps) {
  const { store } = useStore();

  const [topNotifications, setTopNotifications] = useState<INotification[]>(notifications);

  useEffect(() => {
    setTopNotifications(notifications);
  }, [notifications]);

  async function handleMarkAsRead(id: number) {
    await utils.request(store, `notifications/${id}`, "PUT", { status: "read" });
    setTopNotifications(prevNotifications =>
      prevNotifications.map(notif => (notif.id === id ? { ...notif, status: "read" } : notif))
    );
  }

  /* -------------------------------- */
  /* RENDER METHODS */
  /* -------------------------------- */

  function render() {
    return (
      <>
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() =>
            onClose(topNotifications?.filter(notification => notification?.status === "unread"))
          }
        ></div>

        <div className="fixed right-2 top-20 h-[90vh] w-80 bg-white shadow-lg z-50 transform transition-transform duration-300 ease-in-out rounded-lg">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">Notifications</h2>
            <Button
              onClick={() =>
                onClose(topNotifications?.filter(notification => notification?.status === "unread"))
              }
              icon="fa-times"
              className="text-gray-600 hover:text-gray-800"
            />
          </div>

          <div className="p-4 overflow-y-auto h-full">
            {topNotifications.length > 0 ? (
              topNotifications.map(notification => (
                <div
                  key={notification.id}
                  className="flex items-start p-3 mb-2 border-b border-gray-200 cursor-pointer"
                  onClick={() => handleMarkAsRead(notification.id)}
                >
                  {/* Icon based on type */}
                  <div className="mr-3">
                    {notification.type === "action" && (
                      <Icon name="fa-circle-check" className="text-green-500 text-lg" />
                    )}
                    {notification.type === "error" && (
                      <Icon name="fa-triangle-exclamation" className="text-red-500 text-lg" />
                    )}
                    {notification.type === "alert" && (
                      <Icon name="fa-circle-exclamation" className="text-blue-500 text-lg" />
                    )}
                  </div>

                  <div className="flex-1">
                    {/* <h3 className="font-semibold text-sm text-gray-800">{utils.titleCase(notification.type)}</h3>
                  <p className="text-gray-600 text-sm">{notification.message}</p> */}
                    <p className="text-sm text-gray-800">
                      <span className="font-semibold">
                        {utils.titleCase(notification.type.replace(/-/g, " "))} :
                      </span>{" "}
                      {notification.message}
                    </p>
                    <div className="text-gray-500 text-xs mt-1">
                      {utils.humanFormatDateWithDay(notification.created)}
                    </div>
                  </div>

                  {/* Unread */}
                  <div className="ml-2">
                    <span
                      className={`h-2 w-2 rounded-full block mt-1 ${notification.status === "unread" ? "bg-blue-500" : "bg-gray-400"}`}
                    ></span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500">No notifications</p>
            )}
          </div>
        </div>
      </>
    );
  }

  return render();
}

export default NotificationPanel;
